import { useCallback, useRef } from 'react';
import { setMenuPosition } from '../services/MenuService';
import { scaleImage, scaleText, scaleGroup, scaleRect } from '../services/ScaleService';
import { SetEditableByDblClick } from '../GraphicEditorHelpers';
import { loadBrowserFont, getRuntimeFontFamily } from '../services/FontsService';
import { attachTransformerToSingleNode } from '../utils/layerSelectionSync';
import rotateCursor from '../../../svg/icon/rotate-cursor.png';
import { findImageBackgroundRect } from '../utils/findImageBackgroundRect';
import { closestElementGroup } from '../utils/closestElementGroup';
import {
  collectSpacingSegmentsToNeighbors,
  unionClientRects,
  getSpacingSegmentsBetweenRects,
  getInsetSegmentsInnerToOuter,
} from '../utils/spacingMeasurements';
import { clearMeasureOverlays, drawMeasureOverlays } from '../utils/measureOverlays';
import { isAltModifierKeyUp } from '../utils/measureModifier';
import { pickElementGroupUnderPointerFigma } from '../utils/measureTargetResolve';
import { getParentContainerRectForSelection } from '../utils/measureParentBounds';
const Konva = require('konva');

export const useStageSetup = ({
  canvasSize,
  canvasPosition,
  presetsDataSource,
  sidebarWidth,
  layerDiff,
  maxDepth,
  fonts,
  setStage,
  setCanvasSize,
  setCanvasPosition,
  setPresetsDataSource,
  setPreset,
  setDesignName,
  setSelectedGroup,
  setSelectedElementPosition,
  refreshProperties,
  refreshLayersStructure,
  handleTransformEnd,
  moveAttachedSources,
  updateSourceAttachedOffset,
  saveHistory,
  toggleProductPropertiesPanel,
}) => {
  const handleTransformEndRef = useRef(handleTransformEnd);
  handleTransformEndRef.current = handleTransformEnd;

  const createStage = useCallback(() => {
    const stageWidth = 'calc(100% - 210px - 2rem)';
    const stageHeight = 'calc(100% - 4rem)';

    const stage = new Konva.Stage({
      width: stageWidth,
      height: stageHeight,
      container: 'konva_container',
      draggable: true,
      canvas_width: canvasSize.width,
      canvas_height: canvasSize.height,
      preset_name: presetsDataSource[0].name,
    });

    const canvasLayer = new Konva.Layer({
      id: 'canvas',
      clip: {
        x: canvasPosition.x,
        y: canvasPosition.y,
        width: canvasSize.width,
        height: canvasSize.height,
      },
    });

    canvasLayer.add(
      new Konva.Rect({
        id: 'canvas_bg',
        stroke: 'gray',
        strokeWidth: 1,
        opacity: 1,
        fill: 'white',
        x: canvasPosition.x,
        y: canvasPosition.y,
        width: canvasSize.width,
        height: canvasSize.height,
        listening: false,
      }),
    );

    stage.add(canvasLayer);
    initStageFunctions(stage, canvasLayer);
    setDesignName('noname template');
    return stage;
  }, [
    canvasSize,
    canvasPosition,
    presetsDataSource,
    setDesignName,
    handleTransformEnd,
    setSelectedGroup,
    refreshLayersStructure,
    layerDiff,
    moveAttachedSources,
    updateSourceAttachedOffset,
    refreshProperties,
    saveHistory,
    setPresetsDataSource,
    setPreset,
  ]);

  const loadStage = useCallback(
    (design) => {
      if (!design?.design_json) return;

      const stage = Konva.Node.create(design.design_json, 'konva_container');

      setCanvasPosition(stage.findOne('#canvas_bg').position());
      setCanvasSize({
        width: stage.getAttr('canvas_width'),
        height: stage.getAttr('canvas_height'),
      });

      stage.width(window.innerWidth - sidebarWidth);
      stage.height(window.innerHeight);
      stage.findOne('#canvas_bg').setAttrs({ stroke: 'gray', strokeWidth: 1 });

      const textsList = stage.find('Text');
      const imagesList = stage.find('Image');
      const canvasLayer = stage.findOne('#canvas');

      if (textsList.length) {
        for (let text of textsList) {
          const isDynamicText = text.getAttr('dynamicField');
          if (!isDynamicText) {
            SetEditableByDblClick(text, stage, () => refreshProperties());
          }

          const fontUrl = text.getAttr('fontUrl');
          const savedFamily = text.getAttr('fontFamily');
          const fontsList = fonts || [];
          const matchedFont =
            fontsList.find((f) => f.font === fontUrl) ||
            fontsList.find((f) => getRuntimeFontFamily(f) === savedFamily);
          const family = matchedFont
            ? getRuntimeFontFamily(matchedFont)
            : savedFamily;
          const resolvedFontUrl = matchedFont?.font || fontUrl || '';

          text.setAttr('fontUrlTemp', resolvedFontUrl);
          text.setAttr('fontUrl', '');
          text.setAttr('fontFamilyOriginal', family);
          text.setAttr('fontFamily', 'Arial');

          const fontToLoad = matchedFont || { family, font: resolvedFontUrl };
          loadBrowserFont(fontToLoad)
            .then((ok) => updateTextFont(text, ok))
            .catch((e) => {
              console.error('Error loading font', family, e);
              updateTextFont(text, false);
            });
        }
      }

      if (imagesList.length) {
        for (let imgEl of imagesList) {
          if (imgEl.getAttr('dynamicField')) {
            continue;
          }
          const group = imgEl.parent;
          const rect = findImageBackgroundRect(group);
          if (!rect) continue;
          const imgAttrs = imgEl.getAttrs();
          const imageObj = new window.Image();
          imageObj.onload = function () {
            const oldImage = imgAttrs.image;
            delete imgAttrs.image;
            delete imgAttrs.width;
            delete imgAttrs.height;
            const imgNode = new Konva.Image({
              ...imgAttrs,
              image: imageObj,
              initWidth: imageObj.width,
              initHeight: imageObj.height,
            });
            rect.fillPatternImage(imageObj);
            group.add(imgNode);
            if (oldImage) oldImage.remove();
            imgEl.destroy();
            scaleImage(imgNode);
            canvasLayer.draw();
          };
          imageObj.src = imgAttrs.url;
        }
      }

      const unnecessaryTransformer = stage.findOne('Transformer');
      if (unnecessaryTransformer) unnecessaryTransformer.destroy();

      initStageFunctions(stage, canvasLayer);
      return stage;
    },
    [
      sidebarWidth,
      fonts,
      setCanvasPosition,
      setCanvasSize,
      handleTransformEnd,
      setSelectedGroup,
      refreshLayersStructure,
      layerDiff,
      moveAttachedSources,
      updateSourceAttachedOffset,
      refreshProperties,
      saveHistory,
      setPresetsDataSource,
      setPreset,
    ],
  );

  const updateTextFont = (text, loadSuccess) => {
    const fontUrl = text.getAttr('fontUrlTemp') || '';
    text.setAttr('fontUrl', fontUrl);
    text.setAttr('fontUrlTemp', '');
    const fontFamilyOriginal = text.getAttr('fontFamilyOriginal');
    text.setAttr('fontFamily', fontFamilyOriginal);
    text.setAttr('fontFamilyOriginal', '');
    try {
      const layer = text.getLayer();
      if (layer) layer.draw();
    } catch (e) {
      console.warn('Font redraw error:', e);
    }
  };

  const initStageFunctions = (stage, layer) => {
    setupTransformer(stage, layer);
    setupGuidLines(stage, layer);
    setupMeasureSpacing(stage, layer);
    setupDragListeners(layer);
    setupOutlineLayer(stage);
    setupCaptionLayer(stage);
    setupZoomOnScroll(stage);
    setupPresetData(stage);
  };

  const setupTransformer = (stage, layer) => {
    const tr = new Konva.Transformer({
      elementType: 'group',
      rotationSnaps: [0, 90, 180, 270],
    });

    tr.boundBoxFunc((oldBox, newBox) => {
      if (newBox.width < 5 || newBox.height < 5) return oldBox;
      return newBox;
    });

    tr.on('mouseenter', (e) => {
      const anchor = e.target;
      if (anchor.getAttr('name')?.indexOf('rotater') > -1 && anchor.getStage()?.content) {
        anchor.getStage().content.style.cursor = `url("${rotateCursor}") 8 8, auto`;
      }
    });

    tr.on('mouseout', (e) => {
      const anchor = e.target;
      if (anchor.getAttr('name')?.indexOf('rotater') > -1 && anchor.getStage()?.content) {
        anchor.getStage().content.style.cursor = '';
      }
    });

    layer.add(tr);

    const selectionRectangle = new Konva.Rect({
      draggable: false,
      fill: 'rgba(0,0,255,0.5)',
      visible: false,
      id: 'selectionRectangle',
    });
    layer.add(selectionRectangle);

    const toTop = () => {
      selectionRectangle.moveToTop();
      tr.moveToTop();
    };
    const menuNode = document.getElementById('menu');
    let x1, y1, x2, y2;

    stage.on('mousedown touchstart', (e) => {
      if (e.target !== stage) return;
      x1 = stage.getPointerPosition().x;
      y1 = stage.getPointerPosition().y;
      x2 = x1;
      y2 = y1;
      selectionRectangle.visible(true);
      selectionRectangle.width(0);
      selectionRectangle.height(0);
      toTop();
    });

    stage.on('dragmove', () => {
      const nodes = tr.nodes();
      if (nodes.length === 0) return;
      const backgroundWidth = nodes[0].findOne('.background');
      if (backgroundWidth) {
        setMenuPosition(stage, nodes[0], backgroundWidth, layerDiff);
      }
    });

    stage.on('mousemove touchmove', () => {
      if (!selectionRectangle.visible()) return;
      x2 = stage.getPointerPosition().x;
      y2 = stage.getPointerPosition().y;
      selectionRectangle.setAttrs({
        x: Math.min(x1, x2),
        y: Math.min(y1, y2),
        width: Math.abs(x2 - x1),
        height: Math.abs(y2 - y1),
      });
      toTop();
    });

    stage.on('mouseup touchend', () => {
      if (!selectionRectangle.visible()) return;
      const shapes = stage.find('.background');
      const box = selectionRectangle.getClientRect();
      const selected = shapes.filter((shape) =>
        Konva.Util.haveIntersection(box, shape.getClientRect()),
      );
      const selectedGroups = [];
      for (const el of selected) {
        const group = closestElementGroup(stage, el);
        if (group && selectedGroups.indexOf(group) < 0) selectedGroups.push(group);
      }
      tr.nodes(selectedGroups);
      toTop();
      // Hide before the browser dispatches `click` for this gesture; otherwise the
      // click handler sees selectionRectangle.visible() and clears tr.nodes([]).
      selectionRectangle.visible(false);
      setTimeout(() => {
        menuNode.style.display = 'none';
      });
    });

    stage.on('click tap', function (e) {
      if (selectionRectangle.visible()) {
        menuNode.style.display = 'none';
        tr.nodes([]);
        setSelectedGroup(null);
        refreshLayersStructure();
        return;
      }

      let n = e.target;
      while (n) {
        if (n === tr) return;
        n = n.parent;
      }

      if (e.target === stage) {
        menuNode.style.display = 'none';
        tr.nodes([]);
        setSelectedGroup(null);
        refreshLayersStructure();
        return;
      }

      const target = closestElementGroup(stage, e.target);
      if (!target) {
        menuNode.style.display = 'none';
        tr.nodes([]);
        setSelectedGroup(null);
        refreshLayersStructure();
        return;
      }

      const metaPressed = e.evt.shiftKey || e.evt.ctrlKey || e.evt.metaKey;
      const isSelected = tr.nodes().indexOf(target) >= 0;

      if (!metaPressed) {
        setSelectedGroup(target);
        attachTransformerToSingleNode(tr, target);
        menuNode.style.display = 'initial';
      } else if (metaPressed && isSelected) {
        const nodes = tr.nodes().slice();
        nodes.splice(nodes.indexOf(target), 1);
        tr.nodes(nodes);
        if (tr.nodes().length === 1) setSelectedGroup(tr.nodes()[0]);
        else if (tr.nodes().length > 1) setSelectedGroup(tr);
        else setSelectedGroup(null);
      } else if (metaPressed && !isSelected) {
        const nodes = tr.nodes().concat([target]);
        tr.nodes(nodes);
        if (tr.nodes().length === 1) setSelectedGroup(tr.nodes()[0]);
        else if (tr.nodes().length > 1) setSelectedGroup(tr);
      }
      toTop();
      refreshLayersStructure();
    });

    stage.find('.elementGroup').forEach((el) => {
      el.on('transformend', (e) => handleTransformEndRef.current?.(e));
    });
  };

  const setupGuidLines = (stage, layer) => {
    function getLineGuideStops(skipShape) {
      const vertical = [
        canvasPosition.x + stage.x(),
        canvasPosition.x + stage.x() + canvasSize.width / 2,
        canvasPosition.x + stage.x() + canvasSize.width,
      ];
      const horizontal = [
        canvasPosition.y + stage.y(),
        canvasPosition.y + stage.y() + canvasSize.height / 2,
        canvasPosition.y + stage.y() + canvasSize.height,
      ];
      stage.find('.elementGroup').forEach((guideItem) => {
        if (skipShape instanceof Konva.Transformer) {
          if (skipShape.nodes().indexOf(guideItem) >= 0) return;
        } else if (
          guideItem === skipShape ||
          skipShape.find('.elementGroup').indexOf(guideItem) >= 0
        ) {
          return;
        }
        const box = guideItem.getClientRect();
        vertical.push([box.x, box.x + box.width / 2, box.x + box.width]);
        horizontal.push([box.y, box.y + box.height / 2, box.y + box.height]);
      });
      return { vertical: vertical.flat(), horizontal: horizontal.flat() };
    }

    function getObjectSnappingEdges(node) {
      const box = node.getClientRect();
      const absPos = node.absolutePosition();
      return {
        vertical: [
          { guide: Math.round(box.x), offset: Math.round(absPos.x - box.x), snap: 'start' },
          { guide: Math.round(box.x + box.width / 2), offset: Math.round(absPos.x - box.x - box.width / 2), snap: 'center' },
          { guide: Math.round(box.x + box.width), offset: Math.round(absPos.x - box.x - box.width), snap: 'end' },
        ],
        horizontal: [
          { guide: Math.round(box.y), offset: Math.round(absPos.y - box.y), snap: 'start' },
          { guide: Math.round(box.y + box.height / 2), offset: Math.round(absPos.y - box.y - box.height / 2), snap: 'center' },
          { guide: Math.round(box.y + box.height), offset: Math.round(absPos.y - box.y - box.height), snap: 'end' },
        ],
      };
    }

    function getGuides(lineGuideStops, itemBounds) {
      const resultV = [];
      const resultH = [];
      const GUIDELINE_OFFSET = 5;
      lineGuideStops.vertical.forEach((lineGuide) => {
        itemBounds.vertical.forEach((itemBound) => {
          const diff = Math.abs(lineGuide - itemBound.guide);
          if (diff < GUIDELINE_OFFSET) {
            resultV.push({ lineGuide, diff, snap: itemBound.snap, offset: itemBound.offset });
          }
        });
      });
      lineGuideStops.horizontal.forEach((lineGuide) => {
        itemBounds.horizontal.forEach((itemBound) => {
          const diff = Math.abs(lineGuide - itemBound.guide);
          if (diff < GUIDELINE_OFFSET) {
            resultH.push({ lineGuide, diff, snap: itemBound.snap, offset: itemBound.offset });
          }
        });
      });
      const guides = [];
      const minV = resultV.sort((a, b) => a.diff - b.diff)[0];
      const minH = resultH.sort((a, b) => a.diff - b.diff)[0];
      if (minV) guides.push({ lineGuide: minV.lineGuide, offset: minV.offset, orientation: 'V', snap: minV.snap });
      if (minH) guides.push({ lineGuide: minH.lineGuide, offset: minH.offset, orientation: 'H', snap: minH.snap });
      return guides;
    }

    function drawGuides(guides) {
      guides.forEach((lg) => {
        const isH = lg.orientation === 'H';
        const line = new Konva.Line({
          points: isH ? [-6000, 0, 6000, 0] : [0, -6000, 0, 6000],
          stroke: 'rgb(0, 161, 255)',
          strokeWidth: 1,
          name: 'guid-line',
          dash: [4, 6],
        });
        layer.add(line);
        line.absolutePosition(
          isH ? { x: 0, y: lg.lineGuide } : { x: lg.lineGuide, y: 0 },
        );
      });
    }

    layer.on('dragmove', function (e) {
      const target = e.target;
      const nodes = stage.findOne('Transformer').nodes();
      if (nodes.indexOf(target) >= 0 && !(target instanceof Konva.Transformer)) return;

      layer.find('.guid-line').forEach((l) => l.destroy());
      const lineGuideStops = getLineGuideStops(target);
      const itemBounds = getObjectSnappingEdges(target);
      const guides = getGuides(lineGuideStops, itemBounds);
      if (!guides.length) return;

      drawGuides(guides);
      const absPos = target.absolutePosition();
      guides.forEach((lg) => {
        if (lg.orientation === 'V') absPos.x = lg.lineGuide + lg.offset;
        else absPos.y = lg.lineGuide + lg.offset;
      });
      target.absolutePosition(absPos);

      const backgroundWidth =
        nodes.length === 1 ? nodes[0].findOne('.background') : target.findOne('.back');
      const targetElement = target.hasName('condition') ? target.parent : target;
      if (backgroundWidth) {
        setMenuPosition(
          stage,
          nodes.length === 1 ? nodes[0] : targetElement,
          backgroundWidth,
          layerDiff,
        );
      }
    });

    layer.on('dragend', function () {
      layer.find('.guid-line').forEach((l) => l.destroy());
      refreshProperties();
      saveHistory(stage);
    });
  };

  const setupMeasureSpacing = (stage, layer) => {
    let isElementDrag = false;
    let altSpacingMeasureHeld = false;
    const syncAltSpacingHeld = (evt) => {
      if (evt && 'altKey' in evt) altSpacingMeasureHeld = Boolean(evt.altKey);
    };
    /** @type {{ x: number, y: number } | null} */
    let lastPointerPos = null;

    const transformer = () => stage.findOne('Transformer');

    function clientRectContainsPointPad(r, x, y, pad = 6) {
      return (
        x >= r.x - pad &&
        x <= r.x + r.width + pad &&
        y >= r.y - pad &&
        y <= r.y + r.height + pad
      );
    }

    layer.on('dragstart', (e) => {
      if (e.target === stage) return;
      const g = closestElementGroup(stage, e.target);
      if (g) isElementDrag = true;
    });

    layer.on('dragend', () => {
      isElementDrag = false;
      clearMeasureOverlays(layer);
    });

    layer.on('dragmove', (e) => {
      if (e.evt) stage.setPointersPositions(e.evt);
      lastPointerPos = stage.getPointerPosition() ?? lastPointerPos;
      syncAltSpacingHeld(e.evt);

      if (!altSpacingMeasureHeld) {
        clearMeasureOverlays(layer);
        return;
      }
      if (!isElementDrag) return;
      const pos = lastPointerPos ?? stage.getPointerPosition();
      if (pos) runHoverMeasure(pos, e.evt);
    });

    const runHoverMeasure = (pos, evtLike) => {
      if (!pos) return;
      syncAltSpacingHeld(evtLike);
      if (!altSpacingMeasureHeld) {
        if (!isElementDrag) clearMeasureOverlays(layer);
        return;
      }

      const tr = transformer();
      const nodes = tr.nodes();
      if (!nodes.length) {
        clearMeasureOverlays(layer);
        return;
      }

      const union = unionClientRects(nodes, stage);
      if (!union) {
        clearMeasureOverlays(layer);
        return;
      }

      const canvasBg = stage.findOne('#canvas_bg');
      const canvasRect = canvasBg?.getClientRect({ relativeTo: stage });
      const parentRect = getParentContainerRectForSelection(nodes, stage);
      const parentInsetSegs = parentRect ? getInsetSegmentsInnerToOuter(union, parentRect) : [];
      const canvasInsetSegs = canvasRect ? getInsetSegmentsInnerToOuter(union, canvasRect) : [];

      const selectedSet = new Set(nodes);
      const hovered = pickElementGroupUnderPointerFigma(
        stage,
        pos,
        selectedSet,
        clientRectContainsPointPad,
      );

      const elementGroups = stage.find('.elementGroup');
      const groupsForNeighbors =
        hovered != null ? elementGroups.filter((g) => g !== hovered) : elementGroups;
      const primaryForNeighbors = closestElementGroup(stage, nodes[0]) ?? nodes[0];
      const neighborSegs = collectSpacingSegmentsToNeighbors(
        tr,
        primaryForNeighbors,
        Konva,
        groupsForNeighbors,
        stage,
      );

      let betweenSegs = [];
      /** @type {import('../utils/spacingMeasurements').ClientRect | null} */
      let outlineRect = null;
      if (hovered) {
        const hoveredRect = hovered.getClientRect({ relativeTo: stage });
        betweenSegs = getSpacingSegmentsBetweenRects(union, hoveredRect);
        outlineRect = hoveredRect;
      }

      const allSegs = [...parentInsetSegs, ...canvasInsetSegs, ...neighborSegs, ...betweenSegs];
      if (!allSegs.length && !outlineRect) {
        clearMeasureOverlays(layer);
        return;
      }
      drawMeasureOverlays(layer, stage, allSegs, outlineRect);
    };

    const onStagePointerMove = (e) => {
      if (e.evt) stage.setPointersPositions(e.evt);
      syncAltSpacingHeld(e.evt);
      const pos = stage.getPointerPosition();
      lastPointerPos = pos ?? lastPointerPos;
      if (pos) runHoverMeasure(pos, e.evt);
    };

    stage.on('mousemove', onStagePointerMove);
    stage.on('touchmove', onStagePointerMove);

    const container = stage.container();
    const onLeave = () => {
      lastPointerPos = null;
      clearMeasureOverlays(layer);
    };
    container.addEventListener('mouseleave', onLeave);

    const onKeyUp = (ke) => {
      syncAltSpacingHeld(ke);
      if (!isAltModifierKeyUp(ke)) return;
      clearMeasureOverlays(layer);
    };
    window.addEventListener('keyup', onKeyUp);

    const onKeyDown = (ke) => {
      syncAltSpacingHeld(ke);
      if (!altSpacingMeasureHeld) return;
      if (!lastPointerPos || isElementDrag) return;
      runHoverMeasure(lastPointerPos, ke);
    };
    window.addEventListener('keydown', onKeyDown);

    const onWindowBlur = () => {
      altSpacingMeasureHeld = false;
      clearMeasureOverlays(layer);
    };
    window.addEventListener('blur', onWindowBlur);
  };

  const setupDragListeners = (layer) => {
    layer.on('dragmove', function (e) {
      const target = e.target;
      if (target.hasName('target')) moveAttachedSources(target);
      if (target.hasName('source')) updateSourceAttachedOffset(target);
    });
  };

  const setupOutlineLayer = (stage) => {
    stage.findOne('#outlineLayer')?.destroy();
    const outlineLayer = new Konva.Layer({ id: 'outlineLayer' });
    const bg = stage.findOne('#canvas_bg');
    outlineLayer.add(
      new Konva.Shape({
        id: 'outline_bg',
        x: bg.x(),
        y: bg.y(),
        width: canvasSize.width,
        height: canvasSize.height,
        fill: 'rgba(249,249,249,0.5)',
        sceneFunc: (ctx, shape) => {
          ctx.beginPath();
          const bord = 100000;
          ctx.moveTo(-bord, -bord);
          ctx.lineTo(shape.width() + bord, -bord);
          ctx.lineTo(shape.width() + bord, shape.height() + bord);
          ctx.lineTo(-bord, shape.height() + bord);
          ctx.lineTo(-bord, shape.height());
          ctx.lineTo(shape.width(), shape.height());
          ctx.lineTo(shape.width(), 0);
          ctx.lineTo(0, 0);
          ctx.lineTo(0, shape.height());
          ctx.lineTo(-bord, shape.height());
          ctx.lineTo(-bord, -bord);
          ctx.closePath();
          ctx.fillStrokeShape(shape);
        },
        listening: false,
      }),
    );
    stage.add(outlineLayer);
  };

  const setupCaptionLayer = (stage) => {
    stage.findOne('#captionLayer')?.destroy();
    const captionLayer = new Konva.Layer({ id: 'captionLayer' });
    const bg = stage.findOne('#canvas_bg');
    const text = new Konva.Text({
      x: bg.x(),
      y: bg.y() - 20,
      fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
      fill: '#979797',
      fontSize: 16,
      style: { cursor: 'pointer' },
      text: '',
    });
    text.on('mouseenter', () => { stage.container().style.cursor = 'pointer'; });
    text.on('mouseleave', () => { stage.container().style.cursor = 'default'; });
    text.on('click tap', () => { toggleProductPropertiesPanel(); });
    captionLayer.add(text);
    stage.add(captionLayer);
  };

  const setupZoomOnScroll = (stage) => {
    stage.on('scroll wheel', (e) => {
      e.evt.preventDefault();
      const scaleBy = 1.01;
      scaleStageInner(stage, scaleBy, e.evt.deltaY);
    });
  };

  const scaleStageInner = (stage, scaleBy, deltaY) => {
    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();
    if (!pointer) return;
    const mousePointTo = {
      x: pointer.x / oldScale - stage.x() / oldScale,
      y: pointer.y / oldScale - stage.y() / oldScale,
    };
    const newScale = deltaY > 0 ? oldScale * scaleBy : oldScale / scaleBy;
    stage.scale({ x: newScale, y: newScale });
    const newPos = {
      x: -(mousePointTo.x - pointer.x / newScale) * newScale,
      y: -(mousePointTo.y - pointer.y / newScale) * newScale,
    };
    stage.position(newPos);
    stage.draw();
  };

  const setupPresetData = (stage) => {
    if (!stage) return;
    let presetName = stage.getAttr('preset_name');
    if (presetName) {
      const exist = presetsDataSource.find((t) => t.name === presetName);
      if (!exist) {
        const newItems = [...presetsDataSource];
        newItems.push({
          id: presetName,
          name: presetName,
          width: canvasSize.width,
          height: canvasSize.height,
        });
        setPresetsDataSource(newItems);
      }
    } else {
      presetName = presetsDataSource[presetsDataSource.length - 1].id;
    }
    setPreset(presetName);
  };

  const zoomStage = useCallback(
    (deltaY) => {
      const scaleBy = 1.1;
      scaleStageInner(null, scaleBy, deltaY);
    },
    [],
  );

  return {
    createStage,
    loadStage,
    zoomStage: (stage, deltaY) => {
      const scaleBy = 1.1;
      scaleStageInner(stage, scaleBy, deltaY);
    },
    scaleStage: scaleStageInner,
  };
};
